import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Package, ShoppingBag, DollarSign, TrendingUp, Download, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState("12");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchProductStats();
  }, [months]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/orders`),
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats/products`, {
        params: { months: parseInt(months) }
      });
      setProductStats(response.data);
    } catch (error) {
      console.error("Error fetching product stats:", error);
    }
  };

  const handleExport = async (type) => {
    try {
      toast.info("Generando exportación...");
      const response = await axios.get(`${API}/admin/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivo descargado");
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statCards = [
    {
      title: "Clientes",
      value: stats?.total_users || 0,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Productos",
      value: stats?.total_products || 0,
      icon: Package,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      title: "Pedidos",
      value: stats?.total_orders || 0,
      icon: ShoppingBag,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      title: "Pendientes",
      value: stats?.pending_orders || 0,
      icon: TrendingUp,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const maxRevenue = productStats?.products?.[0]?.total_revenue || 1;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Panel de Administración
          </h1>
          <p className="text-slate-500 mt-1">
            Resumen general de tu negocio
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {loading
            ? [...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-10 w-10 rounded-lg mb-3" />
                    <Skeleton className="h-8 w-16 mb-1" />
                    <Skeleton className="h-4 w-20" />
                  </CardContent>
                </Card>
              ))
            : statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card key={stat.title} className="bg-white shadow-sm">
                    <CardContent className="p-6">
                      <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center mb-3`}>
                        <Icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                      <p className="text-3xl font-bold text-slate-900">
                        {stat.value}
                      </p>
                      <p className="text-sm text-slate-500">{stat.title}</p>
                    </CardContent>
                  </Card>
                );
              })}
        </div>

        {/* Revenue card */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-7 h-7" />
              </div>
              <div>
                <p className="text-slate-300 text-sm">Ingresos Totales</p>
                {loading ? (
                  <Skeleton className="h-10 w-32 bg-white/20" />
                ) : (
                  <p className="text-4xl font-bold">
                    {`${(stats?.total_revenue || 0).toFixed(2)}€`}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export section */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="w-5 h-5" />
              Exportar Datos (CSV)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                variant="outline"
                onClick={() => handleExport('clients')}
                className="gap-2"
                data-testid="export-clients-btn"
              >
                <Users className="w-4 h-4" />
                Clientes
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('products')}
                className="gap-2"
                data-testid="export-products-btn"
              >
                <Package className="w-4 h-4" />
                Productos
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('orders')}
                className="gap-2"
                data-testid="export-orders-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                Pedidos
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('assignments')}
                className="gap-2"
                data-testid="export-assignments-btn"
              >
                <TrendingUp className="w-4 h-4" />
                Asignaciones
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              También disponible: <code className="bg-slate-100 px-1 rounded">/api/admin/export/orders-detail</code> para análisis detallado por producto
            </p>
          </CardContent>
        </Card>

        {/* Revenue by Product Chart */}
        <Card className="bg-white shadow-sm" data-testid="revenue-chart-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Ingresos por Producto
                </CardTitle>
                <CardDescription className="mt-1">
                  Dinero generado por cada producto (sin IVA)
                </CardDescription>
              </div>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="w-[180px]" data-testid="months-select">
                  <SelectValue placeholder="Seleccionar período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Último mes</SelectItem>
                  <SelectItem value="3">Últimos 3 meses</SelectItem>
                  <SelectItem value="6">Últimos 6 meses</SelectItem>
                  <SelectItem value="12">Último año</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {!productStats ? (
              <div className="space-y-3">
                <Skeleton className="h-[300px] w-full" />
              </div>
            ) : productStats.products?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <BarChart3 className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay datos de ventas en este período</p>
                <p className="text-sm text-slate-400 mt-1">Los ingresos aparecerán aquí cuando se realicen pedidos</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="h-[350px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productStats.products.slice(0, 15)}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis 
                        type="number" 
                        tickFormatter={(value) => `${value.toFixed(0)}€`}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="product_name" 
                        width={120}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value.toFixed(2)}€`, 'Ingresos']}
                        labelFormatter={(label) => `Producto: ${label}`}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Bar 
                        dataKey="total_revenue" 
                        radius={[0, 4, 4, 0]}
                      >
                        {productStats.products.slice(0, 15).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={index === 0 ? '#1e40af' : index < 3 ? '#3b82f6' : '#93c5fd'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(productStats.total_revenue || 0)}</p>
                    <p className="text-xs text-slate-500">Ingresos Totales</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{productStats.total_orders || 0}</p>
                    <p className="text-xs text-slate-500">Pedidos</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{productStats.products?.length || 0}</p>
                    <p className="text-xs text-slate-500">Productos Vendidos</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">
                      {productStats.total_orders > 0 
                        ? formatCurrency((productStats.total_revenue || 0) / productStats.total_orders)
                        : '0€'
                      }
                    </p>
                    <p className="text-xs text-slate-500">Media por Pedido</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent orders */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Últimos Pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : recentOrders.length === 0 ? (
              <p className="text-slate-500 text-center py-8">
                No hay pedidos todavía
              </p>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div
                    key={order.order_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`recent-order-${order.order_id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {order.user_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {order.user_company || "Sin empresa"} · {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {order.total.toFixed(2)}€
                      </p>
                      <p className="text-xs text-slate-500">
                        {order.items.length} productos
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
